import React, { useState, useEffect } from 'react';
import axios from 'axios';

const FacilityAssignmentModal = ({
  isOpen,
  onClose,
  onSuccess,
  gym,
  fromStage,
  toStage
}) => {
  const [facilityUsers, setFacilityUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [deadline, setDeadline] = useState('');
  const [notes, setNotes] = useState('');
  const [rekiVisitDate, setRekiVisitDate] = useState('');
  const [rekiTeamNotes, setRekiTeamNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Task selection state
  const [predefinedTasks, setPredefinedTasks] = useState([]);
  const [tasksByCategory, setTasksByCategory] = useState({});
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (isOpen && gym) {
      fetchFacilityUsers();
      fetchPredefinedTasks();
      // Set default deadline to 7 days from now
      const defaultDeadline = new Date();
      defaultDeadline.setDate(defaultDeadline.getDate() + 7);
      setDeadline(defaultDeadline.toISOString().slice(0, 16));
    }
  }, [isOpen, gym]);

  const fetchFacilityUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/checklists/facility-users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.users && response.data.users.length > 0) {
        setFacilityUsers(response.data.users);
        setSelectedUserId(response.data.users[0].id); // Auto-select first user
      } else {
        setError('No facility team members found. Please contact an administrator.');
      }
    } catch (error) {
      console.error('Error fetching facility users:', error);
      setError('Failed to fetch facility team members');
    } finally {
      setLoading(false);
    }
  };

  const fetchPredefinedTasks = async () => {
    try {
      setLoadingTasks(true);
      const response = await axios.get('/api/predefined-tasks', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.tasks) {
        setPredefinedTasks(response.data.tasks);
        setTasksByCategory(response.data.tasksByCategory || {});
      }
    } catch (error) {
      console.error('Error fetching predefined tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };

  const selectAllInCategory = () => {
    if (!selectedCategory) return;
    const categoryTasks = predefinedTasks.filter(t => (t.category || 'Other') === selectedCategory);
    const categoryTaskIds = categoryTasks.map(t => t.id);
    const allSelected = categoryTaskIds.every(id => selectedTasks.includes(id));

    if (allSelected) {
      setSelectedTasks(prev => prev.filter(id => !categoryTaskIds.includes(id)));
    } else {
      setSelectedTasks(prev => [...new Set([...prev, ...categoryTaskIds])]);
    }
  };

  const getCategoryTaskCounts = () => {
    const counts = {};
    predefinedTasks.forEach(task => {
      const cat = task.category || 'Other';
      if (!counts[cat]) counts[cat] = { total: 0, selected: 0 };
      counts[cat].total++;
      if (selectedTasks.includes(task.id)) counts[cat].selected++;
    });
    return counts;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!selectedUserId) {
      setError('Please select a facility team member');
      return;
    }

    if (!deadline) {
      setError('Please set a deadline');
      return;
    }

    if (selectedTasks.length === 0) {
      setError('Please select at least one task to assign');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Get selected task details
      const tasksToAssign = predefinedTasks
        .filter(t => selectedTasks.includes(t.id))
        .map(t => ({
          name: t.name,
          description: t.description,
          category: t.category
        }));

      // Assign selected tasks to the facility team member
      const bulkAssignResponse = await axios.post('/api/facility-task/bulk-assign', {
        gym_id: gym.id,
        assigned_to: selectedUserId,
        deadline: deadline,
        notes: notes,
        tasks: tasksToAssign, // Send selected tasks
        ...(rekiVisitDate && { rekiVisitDate }),
        ...(rekiTeamNotes && { rekiTeamNotes })
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Then, move the gym to Facility Work stage
      const moveGymResponse = await axios.put(`/api/gyms/${gym.id}`, {
        stage: toStage
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Call success handler with updated gym data
      onSuccess({
        gym: moveGymResponse.data.gym,
        tasksAssigned: bulkAssignResponse.data.totalTasks,
        assignedTo: bulkAssignResponse.data.assignedTo
      });

    } catch (error) {
      console.error('Error during facility assignment:', error);
      if (error.response?.data?.existingTasks) {
        setError(`Some tasks already exist for this gym: ${error.response.data.existingTasks.join(', ')}`);
      } else {
        setError(error.response?.data?.error || 'Failed to assign tasks and move gym');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedUserId('');
      setDeadline('');
      setNotes('');
      setRekiVisitDate('');
      setRekiTeamNotes('');
      setError(null);
      setSelectedTasks([]);
      setSelectedCategory(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  const categoryTaskCounts = getCategoryTaskCounts();
  const categories = Object.keys(categoryTaskCounts).sort();
  const currentCategoryTasks = selectedCategory
    ? predefinedTasks.filter(t => (t.category || 'Other') === selectedCategory)
    : [];
  const allInCategorySelected = selectedCategory && currentCategoryTasks.length > 0 &&
    currentCategoryTasks.every(t => selectedTasks.includes(t.id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Assign Facility Tasks</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={submitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-md">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-purple-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm text-purple-800 font-medium">Moving to Facility Work</p>
              <p className="text-xs text-purple-700 mt-1">
                Moving <strong>{gym?.name}</strong> from <strong>{fromStage}</strong> to <strong>{toStage}</strong>
              </p>
              <p className="text-xs text-purple-700 mt-1">
                Select the tasks you want to assign to the facility team member.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column - Assignment Details */}
            <div className="space-y-4">
              {/* Select Facility Team Member */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Facility Team Member <span className="text-red-500">*</span>
                </label>
                {loading ? (
                  <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50">
                    <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                  </div>
                ) : (
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    required
                    disabled={submitting}
                  >
                    <option value="">Select team member...</option>
                    {facilityUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Deadline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Deadline <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                  disabled={submitting}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any special instructions..."
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  disabled={submitting}
                />
              </div>

              {/* Reki Visit Schedule */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Reki Visit Schedule (Optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Reki Visit Date &amp; Time</label>
                    <input
                      type="datetime-local"
                      value={rekiVisitDate}
                      onChange={e => setRekiVisitDate(e.target.value)}
                      disabled={submitting}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-red-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Team Notes</label>
                    <input
                      type="text"
                      value={rekiTeamNotes}
                      onChange={e => setRekiTeamNotes(e.target.value)}
                      placeholder="e.g. Check electrical panel first"
                      disabled={submitting}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-red-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Selected Tasks Summary */}
              <div className="bg-green-50 p-3 rounded-md border border-green-200">
                <p className="text-sm font-medium text-green-800">
                  Selected Tasks: <span className="text-lg">{selectedTasks.length}</span>
                </p>
                <p className="text-xs text-green-700 mt-1">
                  {selectedTasks.length === 0
                    ? 'Please select tasks from the list on the right'
                    : `${selectedTasks.length} task(s) will be assigned to the facility team`
                  }
                </p>
              </div>
            </div>

            {/* Right Column - Task Selection with Category Dropdown */}
            <div className="border border-gray-200 rounded-lg">
              {/* Header */}
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-1">Select Tasks to Assign</h3>
                <p className="text-xs text-gray-500">Choose a category and select tasks</p>
              </div>

              {/* Category Dropdown */}
              <div className="p-4 border-b border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Category <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value || null)}
                    className="w-full appearance-none bg-white border-2 border-orange-300 rounded-lg px-4 py-3 pr-10 text-sm text-gray-900 font-medium focus:ring-2 focus:ring-orange-500 focus:border-orange-500 cursor-pointer"
                  >
                    <option value="">-- Select a Category --</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category} ({categoryTaskCounts[category]?.selected || 0}/{categoryTaskCounts[category]?.total || 0} selected)
                      </option>
                    ))}
                  </select>
                  <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Tasks Display Area */}
              <div className="min-h-[280px] max-h-[320px] overflow-y-auto">
                {loadingTasks ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
                  </div>
                ) : !selectedCategory ? (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p className="text-gray-500 font-medium">Select a category from dropdown</p>
                    <p className="text-gray-400 text-sm mt-1">Tasks will appear here</p>
                  </div>
                ) : (
                  <div className="p-4">
                    {/* Category Header with Select All */}
                    <div className="flex items-center justify-between mb-4 pb-3 border-b">
                      <h4 className="font-semibold text-gray-900">{selectedCategory}</h4>
                      <button
                        type="button"
                        onClick={selectAllInCategory}
                        className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                      >
                        {allInCategorySelected ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    {/* Tasks Grid */}
                    <div className="space-y-2">
                      {currentCategoryTasks.map(task => {
                        const isSelected = selectedTasks.includes(task.id);
                        return (
                          <label
                            key={task.id}
                            className={`flex items-start p-3 rounded-lg cursor-pointer border-2 transition-all ${
                              isSelected
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleTaskSelection(task.id)}
                              className="mt-0.5 mr-3 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${isSelected ? 'text-purple-900' : 'text-gray-800'}`}>
                                {task.name}
                              </p>
                              {task.description && (
                                <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                              )}
                            </div>
                            {isSelected && (
                              <svg className="w-5 h-5 text-purple-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || loading || selectedTasks.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {submitting && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{submitting ? 'Assigning Tasks...' : `Assign ${selectedTasks.length} Task(s) & Move Gym`}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FacilityAssignmentModal;
